import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AlertTriangle, MoveRight } from 'lucide-react';
import { PRODUCT_CODE } from '../../../enums/productCode.enum';
import { handleError } from '../../../helper/bugsnag';
import { getIsUsingReservations } from '../../../helper/UtilityFunctions';
import { useAppDispatch } from '../../../store/hooks';
import { setAuth } from '../../../store/reducers';
import {
  fetchProductDetailsByCode,
  selectCurrentProduct,
  selectProductStatus,
} from '../../../store/reducers/productReducers';
import type { AppDispatch } from '../../../store/store';
import { Button, InputField, InputLoader, Loading } from '../../atoms';
import { RichTextRenderer } from '../../atoms/RichTextRenderer/RichTextRenderer';
import { ContractCheckbox } from '../ContractCheckbox/ContractCheckbox';
import { TrustSection } from './CheckoutComponents/TrustSection';

type CheckoutDetail = {
  label: string;
  value: string | React.ReactNode;
};

type OnSubmitResult = {
  success: boolean;
  redirectUrl?: string;
  navigateTo?: string;
  error?: any;
};

type CheckoutProps = {
  productCode: PRODUCT_CODE;
  pageTitle?: string;
  itemDetailsTitle: string;
  itemDetails: CheckoutDetail[];
  showContractSection?: boolean;
  contractDoctorStamp?: any;
  showCouponSection?: boolean;
  handleSubmit: (couponCode?: string) => Promise<OnSubmitResult>;
  handleBack?: () => void;
  initialLoading?: boolean;
};

export function Checkout({
  productCode,
  itemDetailsTitle,
  itemDetails,
  contractDoctorStamp,
  showCouponSection = true,
  handleSubmit,
  handleBack,
  initialLoading = false,
}: CheckoutProps) {
  const navigate = useNavigate();
  const isUsingReservations = getIsUsingReservations();
  const dispatch: AppDispatch = useAppDispatch();

  const product = useSelector(selectCurrentProduct);
  const deferredPrice = product ? product.totalPrice - product.price : 0;
  const productStatus = useSelector(selectProductStatus);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [showContractHintOnClick, setShowContractHintOnClick] = useState(false);
  const [contractAccepted, setContractAccepted] = useState(true);

  const isCertificateRequest = productCode.includes('document') && product?.price === 0;
  const submitButtonText = isCertificateRequest ? 'Anfrage absenden' : 'Zur Zahlung';

  const isPrescription =
    productCode === PRODUCT_CODE.PRESCRIPTION_ONLINE || productCode === PRODUCT_CODE.RESERVATION_PRESCRIPTION_ONLINE;

  const isAppointment = productCode.includes('appointment') || productCode.includes('Appointment');

  let deferredPriceLabel = 'Fällig nach der Sprechstunde';

  if (isPrescription) {
    deferredPriceLabel = 'Fällig nach Freigabe';
  } else if (isCertificateRequest) {
    deferredPriceLabel = 'Fällig nach Bearbeitung';
  }

  useEffect(() => {
    if (productCode) {
      dispatch(fetchProductDetailsByCode(productCode));
    }

    if (isAppointment) {
      setContractAccepted(false);
    }
  }, [dispatch, productCode, isPrescription]);

  useEffect(() => {
    if (contractAccepted) {
      setShowContractHintOnClick(false);
    }
  }, [contractAccepted]);

  const formatCurrency = (value: number, currencyCode: string = 'EUR') => {
    return (value / 100).toLocaleString('de-DE', { style: 'currency', currency: currencyCode });
  };

  const productIsReady = productStatus === 'succeeded' && product;

  const handlePaymentAttempt = async () => {
    if (isSubmitting || !productIsReady) {
      return;
    }

    if (!contractAccepted) {
      setShowContractHintOnClick(true);
      document.getElementById('generic-contract-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });

      return;
    }

    setIsSubmitting(true);
    const result = await handleSubmit(couponCode);

    if (result.success) {
      console.log('Submission successful');
    } else {
      setIsSubmitting(false);
      handleError(result.error, { info: 'Error during checkout submission' });
      toast.error('Fehler beim Einreichen der Anfrage. Bitte versuchen Sie es erneut.');
      if (result.error?.message?.response?.status === 401) {
        dispatch(setAuth({ isAuthenticated: false }));
        navigate('/login');
      }
    }
  };

  const isButtonHtmlDisabled = isSubmitting || !productIsReady || initialLoading;
  const displayContractHint = showContractHintOnClick && !contractAccepted && productIsReady && !isSubmitting;

  if (initialLoading && productStatus !== 'succeeded') {
    return <Loading />;
  }

  return (
    <div className='pb-6'>
      <div className='md:mt-6 md:grid md:grid-cols-2 md:gap-x-8 lg:gap-x-12'>
        {/* --- LEFT COLUMN --- */}
        <div className='flex flex-col space-y-6'>
          <div className='border border-gray-200 rounded-lg p-4 sm:p-6 shadow-md'>
            <h3 className='text-lg font-semibold text-gray-700 mb-4'>{itemDetailsTitle}</h3>
            <div className='divide-y divide-gray-200'>
              {itemDetails.map((detail, index) => (
                <div key={detail.label + '-' + index} className='flex justify-between items-center py-3'>
                  <span className='text-sm font-medium text-gray-600'>{detail.label}</span>
                  <span className='text-sm text-gray-900 font-semibold text-right'>{detail.value}</span>
                </div>
              ))}
            </div>
            {product && product.checkoutDescription && (
              <RichTextRenderer
                htmlString={product.checkoutDescription}
                className='mt-6 pt-6 border-t border-gray-200 space-y-4 text-sm text-gray-700'
              />
            )}
          </div>
          {isAppointment ? (
            <div
              id='generic-contract-section'
              className={`border rounded-lg p-4 sm:p-6 shadow-md transition-all duration-300 ease-in-out
                                ${displayContractHint ? 'border-red-500 ring-2 ring-red-100' : 'border-gray-200'}`}
            >
              <h3 className='text-lg font-semibold text-gray-700 mb-4'>Einverständniserklärung & Bedingungen</h3>
              <ContractCheckbox
                checked={contractAccepted}
                setChecked={setContractAccepted}
                doctorStamp={contractDoctorStamp}
              />
              {displayContractHint && (
                <p className='text-sm text-red-600 mt-3 flex items-center'>
                  <AlertTriangle size={18} className='mr-2 shrink-0' />
                  Bitte stimmen Sie hier zu, um fortzufahren.
                </p>
              )}
            </div>
          ) : (
            <>
              <div className='hidden md:block'>
                <Button
                  onClick={handleBack}
                  className='w-full text-primary font-semibold py-2 border-primary hover:border-primary px-3 rounded-lg shadow-md transition duration-150 ease-in-out flex items-center justify-center text-base bg-white hover:bg-primary hover:text-white'
                >
                  Zurück
                </Button>
              </div>
            </>
          )}
        </div>

        {/* --- RIGHT COLUMN (CHECKOUT & ACTIONS) --- */}
        <div className='border border-gray-200 rounded-lg p-4 sm:p-6 shadow-md flex flex-col space-y-2 mt-6 md:mt-0'>
          <div>
            {productStatus === 'loading' && !product && (
              <div className='py-4'>
                <InputLoader />
                <span className='mt-2 block text-sm'>Preis wird geladen...</span>
              </div>
            )}
            {productStatus === 'failed' && (
              <div className='text-red-500 py-4'>Preisinformationen konnten nicht geladen werden.</div>
            )}
            {productIsReady && (
              <div className='p-4 bg-gray-50 rounded-lg'>
                <h3 className='text-xl font-semibold text-gray-800 mb-2'>{product.label}</h3>
                {isUsingReservations && product && deferredPrice > 0 ? (
                  <>
                    <div className='flex justify-between items-center'>
                      <p className='text-sm text-gray-600 mb-1'>{deferredPriceLabel}</p>
                      <span className='text-lg font-medium text-gray-700'>
                        {formatCurrency(deferredPrice, product.currency)}
                      </span>
                    </div>
                    <div className='flex justify-between items-center mb-3'>
                      <p className='text-sm text-gray-600 mb-1'>Sofort fälliger Betrag:</p>
                      <span className='text-2xl font-bold text-primary'>
                        {formatCurrency(product.price, product.currency)}
                      </span>
                    </div>
                    <hr className='border-gray-300 mt-3' />
                    <div className='flex justify-between items-center'>
                      <p className='text-sm text-gray-700'>Gesamtpreis:</p>
                      <span className='text-lg text-gray-800'>
                        {formatCurrency(product.totalPrice, product.currency)}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className='flex justify-between items-center'>
                    <p className='text-sm text-gray-600'>Gesamtbetrag:</p>
                    <span className='text-3xl font-bold text-primary'>
                      {formatCurrency(product.price, product.currency)}
                    </span>
                  </div>
                )}
              </div>
            )}
            {productStatus !== 'loading' && productStatus !== 'failed' && !product && (
              <div className='text-gray-500 py-4'>Preisinformationen nicht verfügbar.</div>
            )}
          </div>
          {showCouponSection && (
            <div>
              <div className='flex items-stretch space-x-2'>
                <InputField onChange={setCouponCode} value={couponCode} placeholder='Gutscheincode eingeben' />
              </div>
            </div>
          )}
          <div>
            {isUsingReservations && product && deferredPrice > 0 && (
              <p key='checkout-text-conditional-paragraph' className='text-xs text-gray-600'>
                {product.checkoutSmallText}
              </p>
            )}
            <p className='text-xs text-gray-600'>
              Bei Fragen oder Problemen,{' '}
              <a href='/contact' className='text-primary-dark hover:underline font-medium'>
                kommen Sie gern auf uns zu
              </a>
              .
            </p>
          </div>

          <TrustSection />
          <div className='flex flex-col items-center'>
            {isSubmitting ? (
              <div key='payment-loading' className='flex items-center justify-center py-3 w-full'>
                <InputLoader /> <span className='ml-2'>Wird bearbeitet...</span>
              </div>
            ) : (
              <Button
                key='payment-button'
                onClick={handlePaymentAttempt}
                disabled={isButtonHtmlDisabled}
                className={`w-full text-white font-semibold mt-2 py-3 px-4 rounded-lg shadow-md transition duration-150 ease-in-out flex items-center justify-center text-base
                                  ${isButtonHtmlDisabled ? 'bg-primary-light cursor-not-allowed opacity-60' : 'bg-primary hover:bg-primary-dark'}
                                  ${!isButtonHtmlDisabled && !contractAccepted ? 'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black' : ''}
                                  `}
                aria-disabled={isButtonHtmlDisabled || !contractAccepted}
              >
                {submitButtonText}
                <MoveRight size={18} className='ml-2 shrink-0' />
              </Button>
            )}
            {isPrescription && (
              <div className='w-full mt-3 md:hidden text-center'>
                <Button
                  onClick={handleBack}
                  className={`w-full text-primary font-semibold py-2 border-primary hover:border-primary px-3 rounded-lg shadow-md transition duration-150 ease-in-out flex items-center justify-center text-base bg-white hover:bg-primary hover:text-white`}
                >
                  Zurück
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
